


import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';

// Strips HTML tags so rich-text descriptions show as plain text in this
// compact card preview (line-clamp-4 is designed for plain text, not markup).
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li, br').forEach((el) => {
    el.insertAdjacentText('afterend', ' ');
  });
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

// ---- Impression tracking: one batched request for all cards on screen ----
const seenThisSession = new Set();
let pendingImpressions = new Set();
let flushTimer = null;
const flushImpressions = () => {
  flushTimer = null;
  if (!pendingImpressions.size) return;
  const ids = Array.from(pendingImpressions);
  pendingImpressions = new Set();
  api.post('/jobs/impressions', { job_ids: ids }).catch(() => {});
};
const queueImpression = (id) => {
  if (seenThisSession.has(id)) return;
  seenThisSession.add(id);
  pendingImpressions.add(id);
  if (!flushTimer) flushTimer = setTimeout(flushImpressions, 3000);
};
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushImpressions);
}

// ---- Click tracking: one request per job per session ----
const clickedThisSession = new Set();
const trackClick = (id) => {
  if (!id || clickedThisSession.has(id) || !localStorage.getItem('token')) return;
  clickedThisSession.add(id);
  api.post('/jobs/click', { job_id: id }).catch(() => {});
};

function AnimatedCount({ value, duration = 700 }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (value == null) return;
    let raf;
    let start = null;

    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      setDisplay(Math.floor(progress * value));
      if (progress < 1) raf = requestAnimationFrame(step);
      else setDisplay(value);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);

  return <>{display}</>;
}

// Small breakdown bar for one match component (location / career / skills).
// `max` is the component's weight (30 or 35) so the fill % is relative to
// that component's own ceiling, not the overall 100-point total.
function MatchBreakdownBar({ label, value, max, color }) {
  if (value == null) return null;
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-semibold text-gray-500 w-14 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-[10px] font-semibold text-gray-400 w-10 text-right tabular-nums">
        {value}/{max}
      </span>
    </div>
  );
}

function JobCard({ job, matchScore, matchBreakdown, matchReasons, applied, applying, onApply, logoSrc }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(!!job.saved);
  const [saving, setSaving] = useState(false);
  const cardRef = useRef(null);

  // Count an impression once the card is >=60% visible for 1s
  useEffect(() => {
    const el = cardRef.current;
    if (!el || !job?.id || !localStorage.getItem('token')) return;
    let timer = null;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        timer = setTimeout(() => { queueImpression(job.id); io.disconnect(); }, 1000);
      } else if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }, { threshold: 0.6 });
    io.observe(el);
    return () => { if (timer) clearTimeout(timer); io.disconnect(); };
  }, [job?.id]);

  const jobUrl = `${window.location.origin}/jobs/${job.id}`;

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(jobUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Native share sheet when available, otherwise fall back to copying the link
  const handleShare = async (e) => {
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({ title: job.title, url: jobUrl });
      } catch (err) {
        // user cancelled the share sheet — nothing to do
      }
    } else {
      await navigator.clipboard.writeText(jobUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    if (saving) return;
    setSaving(true);
    try {
      if (saved) {
        await api.delete('/jobs/save', { data: { job_posting_id: job.id } });
        setSaved(false);
      } else {
        await api.post('/jobs/save', { job_posting_id: job.id });
        setSaved(true);
      }
    } catch (err) {
      console.error('Failed to update saved status', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={cardRef}
      onClick={() => { trackClick(job.id); setExpanded((e) => !e); }}
      className="pb-job-card cursor-pointer !p-4"
    >
      {/* Top row: logo, tags, title, salary/actions */}
      <div className="flex items-start gap-3">
        {logoSrc ? (
          <img src={logoSrc} alt="" className="w-9 h-9 rounded-full object-cover shrink-0 border border-gray-100" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center shrink-0 border border-gray-100">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <rect x="4" y="3" width="16" height="18" rx="1" />
              <path d="M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1" />
              <path d="M9 21v-4h6v4" />
            </svg>
          </div>
        )}
        <div className="flex-1 min-w-0">
          {(job.is_urgent || job.is_new) && (
            <div className="flex items-center gap-2 mb-1">
              {job.is_urgent && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-500">
                  Urgent
                </span>
              )}
              {job.is_new && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-500">
                  New
                </span>
              )}
              {job.is_featured_active && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-pb-lime text-pb-dark">
                  ⚡ Boosted
                </span>
              )}
            </div>
          )}

          <div className="flex items-start justify-between gap-3">
            <h3 className="font-heading font-bold text-pb-text leading-snug text-sm flex flex-wrap items-baseline gap-x-1.5">
              <span>{job.title}</span>
              <span className="font-normal text-xs text-gray-500">
                {job.company_name}
                {job.location && <span> · {job.location}</span>}
              </span>
            </h3>

            <div className="flex items-center gap-1 shrink-0">
              <button
                onClick={handleShare}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-700"
                title={copied ? 'Copied!' : 'Share'}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L7.04 9.81C6.5 9.31 5.79 9 5 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
                </svg>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-lg disabled:opacity-50 ${
                  saved ? 'text-pb-purple' : 'text-gray-700'
                }`}
                title={saved ? 'Saved (click to unsave)' : 'Save'}
              >
                {saved ? '★' : '☆'}
              </button>
              {job.salary_max && (
                <span className="text-base font-bold text-gray-900 ml-1 whitespace-nowrap">
                  ₹{Math.round(job.salary_max / 1000)}K
                </span>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            {job.work_mode && <span>{job.work_mode.replace(/_/g, ' ')}</span>}
            {job.job_type && <span>· {job.job_type.replace(/_/g, ' ')}</span>}
            {job.hours_per_week && <span>· {job.hours_per_week} hrs/week</span>}
          </p>
        </div>
      </div>

      {/* View job button row */}
      <div className="flex items-center justify-between mt-1 gap-2">
        {matchScore != null && (
          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 flex-wrap">
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
              style={{ backgroundColor: matchScore >= 80 ? '#10B981' : matchScore >= 50 ? '#7047FF' : '#F59E0B' }}
            >
              {matchScore}
            </span>
            Your match!
            <span className="flex flex-wrap gap-1 ml-1">
              {matchReasons?.locationMatch && (
                <span className="flex items-center gap-0.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">
                  ✓ {matchReasons.matchedLocation || 'Preferred location'}
                </span>
              )}
              {matchReasons?.roleMatch && (
                <span className="flex items-center gap-0.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
                  ✓ Role fits your experience
                </span>
              )}
              {matchReasons?.matchedSkills?.slice(0, 2).map((s, i) => (
                <span key={i} className="flex items-center gap-0.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-600">
                  ✓ {s}
                </span>
              ))}
            </span>
          </div>
        )}

        {(job.applications_count != null || job.views_count != null) && (
          <div className="flex items-center gap-3 text-[11px] text-gray-400 shrink-0">
            {job.applications_count != null && (
              <span className="flex items-center gap-1">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <AnimatedCount value={job.applications_count} /> applied
              </span>
            )}
            {job.views_count != null && (
              <span className="flex items-center gap-1">
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <AnimatedCount value={job.views_count} /> views
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <Link
            to={`/jobs/${job.id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-xs font-semibold text-gray-500 hover:text-gray-700 hover:underline"
          >
            View job
          </Link>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onApply(e);
            }}
            disabled={applying || applied}
            className={`text-xs font-bold px-4 py-2 rounded-full transition ${
              applied
                ? 'bg-green-50 text-green-600 cursor-default'
                : 'bg-pb-purple text-white hover:bg-[#5B35E0] disabled:opacity-50'
            }`}
          >
            {applied ? '✓ Applied' : applying ? 'Applying...' : 'Apply'}
          </button>
        </div>
      </div>

      {/* Expanded: 3-column layout — meta list / description / share panel */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-[140px_1fr_180px] gap-4">
          {/* Left: meta list */}
          <div className="space-y-1.5 text-xs text-gray-500">
            {job.work_mode && <p>{job.work_mode.replace(/_/g, ' ')}</p>}
            {job.job_type && <p>{job.job_type.replace(/_/g, ' ')}</p>}
            {job.hours_per_week && <p>{job.hours_per_week} hrs per week</p>}
            {job.duration && <p>{job.duration}</p>}
            {job.date_range && <p>{job.date_range}</p>}

            {job.experience_required !== undefined && job.experience_required !== null && (
              <p>
                {job.experience_required === 0
                  ? 'No experience required'
                  : `${job.experience_required}+ yrs exp`}
              </p>
            )}
            {job.salary_min && job.salary_max ? (
              <p>₹{Math.round(job.salary_min / 1000)}K - ₹{Math.round(job.salary_max / 1000)}K</p>
            ) : job.salary_max ? (
              <p>Up to ₹{Math.round(job.salary_max / 1000)}K</p>
            ) : null}
            {job.openings && (
              <p>{job.openings} opening{job.openings > 1 ? 's' : ''}</p>
            )}

            <Link
              to={`/jobs/${job.id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-xs font-semibold text-gray-900 mt-2 inline-flex items-center gap-1 hover:underline"
            >
              View job ↗
            </Link>
          </div>

          {/* Middle: description + match breakdown */}
          <div className="min-w-0 space-y-3">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase mb-1">What you'll be working on</p>
              <p className="text-sm text-gray-600 leading-relaxed line-clamp-4 break-words">
                {stripHtml(job.description)}
              </p>
            </div>

            {matchBreakdown && (
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Why this match</p>
                <div className="space-y-1.5">
                  <MatchBreakdownBar
                    label="Skills"
                    value={matchBreakdown.skills?.applicable ? matchBreakdown.skills?.score : null}
                    max={matchBreakdown.skills?.max ?? 30}
                    color="#10B981"
                  />
                  <MatchBreakdownBar
                    label="Experience"
                    value={matchBreakdown.experience?.applicable ? matchBreakdown.experience?.score : null}
                    max={matchBreakdown.experience?.max ?? 20}
                    color="#2563EB"
                  />
                  <MatchBreakdownBar
                    label="Location"
                    value={matchBreakdown.location?.applicable ? matchBreakdown.location?.score : null}
                    max={matchBreakdown.location?.max ?? 15}
                    color="#8B5CF6"
                  />
                  <MatchBreakdownBar
                    label="Salary"
                    value={matchBreakdown.salary?.applicable ? matchBreakdown.salary?.score : null}
                    max={matchBreakdown.salary?.max ?? 15}
                    color="#F59E0B"
                  />
                  <MatchBreakdownBar
                    label="Education"
                    value={matchBreakdown.education?.applicable ? matchBreakdown.education?.score : null}
                    max={matchBreakdown.education?.max ?? 10}
                    color="#14B8A6"
                  />
                  <MatchBreakdownBar
                    label="Job Type"
                    value={matchBreakdown.job_type?.applicable ? matchBreakdown.job_type?.score : null}
                    max={matchBreakdown.job_type?.max ?? 5}
                    color="#EC4899"
                  />
                  <MatchBreakdownBar
                    label="Industry"
                    value={matchBreakdown.industry?.applicable ? matchBreakdown.industry?.score : null}
                    max={matchBreakdown.industry?.max ?? 5}
                    color="#6366F1"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Right: share panel */}
          <div className="bg-gray-50 rounded-xl p-3 h-fit">
            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L7.04 9.81C6.5 9.31 5.79 9 5 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
              </svg>
              Share this job
            </p>
            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <input
                readOnly
                value={jobUrl}
                className="flex-1 min-w-0 text-[11px] border rounded-lg px-2 py-1.5 bg-white text-gray-500 truncate"
              />
              <button
                onClick={handleCopyLink}
                className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-gray-900 text-white whitespace-nowrap shrink-0"
              >
                {copied ? 'Copied!' : 'Copy link'}
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-2 leading-snug">
              Grants are not eligible for referral bonuses at this time.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default JobCard;